import React, { useEffect } from 'react'
import { Button, IconSearch, InputPhone } from '@dostavkee/web-ui'
import dayjs from 'dayjs'
import isEqual from 'lodash.isequal'
import { Controller, useForm } from 'react-hook-form'
import { useDebouncedCallback } from 'use-debounce'

import { ROLES, RolesGuard } from '@/entities/auth'
import { RecruiterDropdownSearch } from '@/entities/recruiter'
import { useDeviceDetect } from '@/shared/model'
import { CalendarRange } from '@/shared/ui/calendar-range'

import type { RecruitmentSearchParams } from '../../model/recruitment-search-params'
import styles from './recruitment-search-form.module.scss'

export interface RecruitmentSearchFormValues
    extends Omit<RecruitmentSearchParams, 'page' | 'from_date' | 'to_date'> {
    date_range?: {
        from?: string
        to?: string
    }
}

interface RecruitmentSearchFormProps {
    searchValues?: Partial<RecruitmentSearchFormValues>
    onSubmit: (values: RecruitmentSearchFormValues) => void
    onReset: () => void
}

export const RecruitmentSearchForm = ({
    searchValues,
    onSubmit,
    onReset,
}: RecruitmentSearchFormProps) => {
    const { handleSubmit, control, watch, setValue, getValues } =
        useForm<RecruitmentSearchFormValues>({
            defaultValues: searchValues,
        })

    const { isMobile, isTablet } = useDeviceDetect()

    const handleReset = React.useCallback(async () => {
        if (isMobile || isTablet) {
            setValue('phone', '')
            setValue('date_range', undefined)
        }

        onReset()
    }, [isMobile, isTablet, onReset, setValue])

    const onDebouncedSubmit = useDebouncedCallback(
        (values: RecruitmentSearchFormValues) => onSubmit(values),
        600
    )

    useEffect(() => {
        if (isMobile || isTablet) {
            return
        }

        const subscription = watch((values) => {
            if (isEqual(values, searchValues)) {
                return
            }

            if (values.phone !== searchValues?.phone) {
                onDebouncedSubmit(values as RecruitmentSearchFormValues)

                return
            }

            onSubmit(values as RecruitmentSearchFormValues)
        })

        return () => subscription.unsubscribe()
    }, [searchValues, isMobile, isTablet, onDebouncedSubmit, onSubmit, watch])

    useEffect(() => {
        if (isMobile || isTablet || !searchValues) {
            return
        }

        const values = getValues()

        if (values.phone !== searchValues?.phone) {
            setValue('phone', searchValues?.phone ?? '')
        }

        if (
            values.date_range?.from !== searchValues.date_range?.from ||
            values.date_range?.to !== searchValues.date_range?.to
        ) {
            setValue('date_range', searchValues.date_range)
        }

        if (values.recruiter_id !== searchValues?.recruiter_id) {
            setValue('recruiter_id', searchValues?.recruiter_id ?? '')
        }
    }, [getValues, searchValues, isMobile, isTablet, setValue])

    return (
        <form className={styles['recruitment-search-form']}>
            <Controller
                control={control}
                name='phone'
                render={({ field }) => (
                    <div className={styles['recruitment-search-form__field']}>
                        <InputPhone
                            {...field}
                            autoComplete='off'
                            label='Номер телефона'
                            postfixEl={<IconSearch color='var(--text-and-icon-secondary)' />}
                            size='large'
                        />
                    </div>
                )}
            />
            <Controller
                control={control}
                name='date_range'
                render={({ field, fieldState }) => {
                    return (
                        <div className={styles['recruitment-search-form__field']}>
                            <CalendarRange
                                {...field}
                                dropdownPosition='right'
                                errorMessage={fieldState.error?.message}
                                isInvalid={fieldState.invalid}
                                label='Период'
                                size='large'
                                value={{
                                    from: field.value?.from
                                        ? dayjs(field.value.from, 'DD-MM-YYYY').toDate()
                                        : undefined,
                                    to: field.value?.to
                                        ? dayjs(field.value.to, 'DD-MM-YYYY').toDate()
                                        : undefined,
                                }}
                                onChange={(value) => {
                                    if (value) {
                                        field.onChange({
                                            from: value.from
                                                ? dayjs(value.from).format('DD-MM-YYYY')
                                                : undefined,
                                            to: value.to
                                                ? dayjs(value.to).format('DD-MM-YYYY')
                                                : undefined,
                                        })
                                    } else {
                                        field.onChange({
                                            from: undefined,
                                            to: undefined,
                                        })
                                    }
                                }}
                            />
                        </div>
                    )
                }}
            />

            <RolesGuard roles={[ROLES.superManager]}>
                <Controller
                    control={control}
                    name='recruiter_id'
                    render={({ field, fieldState }) => (
                        <div className={styles['recruitment-search-form__field']}>
                            <RecruiterDropdownSearch
                                {...field}
                                autoComplete='on'
                                errorMessage={fieldState.error?.message}
                                id='recruiter_id'
                                isInvalid={fieldState.invalid}
                                size='large'
                                onSelect={(option) => {
                                    if (option?.id) {
                                        field.onChange(option.id)
                                    } else {
                                        field.onChange('')
                                    }
                                }}
                            />
                        </div>
                    )}
                />
            </RolesGuard>

            {(isMobile || isTablet) && (
                <div className={styles['recruitment-search-form__controls']}>
                    <Button fullWidth size='medium' variant='secondary' onClick={handleReset}>
                        Сбросить
                    </Button>
                    <Button fullWidth size='medium' onClick={handleSubmit(onSubmit)}>
                        Применить
                    </Button>
                </div>
            )}
        </form>
    )
}
